import {
  ACTIONS,
  CHARTS,
  FIELD_TYPES,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

import {
  calculateLoanPrepayment,
  getInterestSavedTrend,
  getPrincipalComparisonData,
  getOutstandingBalanceComparisonData,
  getInterestComparisonData,
  formatAxisCurrency,
  formatCurrency,
} from "@/utils/finance";

import DataTable from "@/components/calculator/DataTable";

import { Wallet, Percent, Clock3, CreditCard, PiggyBank } from "lucide-react";

import InsightCard from "@/components/calculator/InsightCard";

const prepaymentCalculatorConfig = {
  id: "home-loan-prepayment",

  shortTitle: "Prepayment",

  title: "Home Loan Prepayment Calculator",

  subtitle:
    "Analyze how one-time or recurring prepayments reduce loan tenure, lower total interest, and optimize your home loan repayment strategy.",

  icon: Wallet,

  description:
    "Analyze how one-time or recurring home loan prepayments reduce interest costs, shorten loan tenure, and improve overall repayment efficiency.",

  keywords: [
    "Home Loan Prepayment",
    "Mortgage Prepayment",
    "Loan Foreclosure",
    "Interest Savings",
    "Reduce Loan Tenure",
    "Extra EMI",
    "Home Loan Calculator",
  ],

  fields: [
    {
      key: "loanAmount",
      label: "Loan Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 5000000,
      min: 100000,
      max: 500000000,
      step: 100000,
    },

    {
      key: "annualRate",
      label: "Interest Rate",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 8.5,
      min: 1,
      max: 20,
      step: 0.05,
    },

    {
      key: "tenureYears",
      label: "Loan Tenure",
      type: FIELD_TYPES.NUMBER,
      suffix: "Years",
      defaultValue: 20,
      min: 1,
      max: 40,
      step: 1,
    },

    {
      key: "prepaymentType",
      label: "Prepayment Type",
      type: FIELD_TYPES.SELECT,
      defaultValue: "oneTime",

      options: [
        {
          label: "One Time",
          value: "oneTime",
        },
        {
          label: "Monthly",
          value: "monthly",
        },
        {
          label: "Quarterly",
          value: "quarterly",
        },
        {
          label: "Yearly",
          value: "yearly",
        },
      ],
    },

    {
      key: "prepaymentAmount",
      label: "Prepayment Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 500000,
      min: 1000,
      max: 500000000,
      step: 10000,
    },

    {
      key: "startMonth",
      label: "Prepayment Starts After",
      type: FIELD_TYPES.NUMBER,
      suffix: "Months",
      defaultValue: 12,
      min: 1,
      max: 480,
      step: 1,
    },

    {
      key: "keepEmiConstant",
      label: "Reduce Tenure (Keep EMI Same)",
      type: FIELD_TYPES.SWITCH,
      defaultValue: true,
    },
  ],

  summaryCards: [
    {
      title: "Original Interest",
      key: "originalInterest",
      type: RESULT_TYPES.CURRENCY,
      description: "Total interest payable without any prepayments.",
      icon: Percent,
    },

    {
      title: "Revised Interest",
      key: "revisedInterest",
      type: RESULT_TYPES.CURRENCY,
      description:
        "Interest payable after applying the selected prepayment strategy.",
      icon: CreditCard,
    },

    {
      title: "Interest Saved",
      key: "interestSaved",
      type: RESULT_TYPES.CURRENCY,
      description: "Total interest saved through prepayments.",
      icon: PiggyBank,
    },

    {
      title: "Time Saved",
      key: "monthsSaved",
      displayKey: "timeSavedText",
      type: RESULT_TYPES.NUMBER,
      description: "Reduction in total loan tenure.",
      icon: Clock3,
    },

    {
      title: "Revised EMI",
      key: "revisedEmi",
      type: RESULT_TYPES.CURRENCY,
      description:
        "Updated EMI after prepayment when EMI reduction is selected.",
      icon: CreditCard,
    },

    {
      title: "Total Prepayment",
      key: "totalPrepayment",
      type: RESULT_TYPES.CURRENCY,
      description: "Total additional amount paid towards the principal.",
      icon: Wallet,
    },
  ],

  charts: [
    {
      id: "interest-comparison",

      title: "Interest Comparison",

      description: "Compare total interest before and after prepayments.",

      type: CHARTS.PIE,

      resultsKey: "interestComparison",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,

      minWidth: 580,
    },

    {
      id: "outstanding-balance",

      title: "Outstanding Balance Comparison",

      description:
        "Track outstanding loan balance with and without prepayments.",

      type: CHARTS.LINE,

      resultsKey: "outstandingBalanceComparison",

      minWidth: 700,

      xAxis: {
        dataKey: "month",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      lines: [
        {
          dataKey: "originalBalance",
          name: "Original",
        },
        {
          dataKey: "revisedBalance",
          name: "With Prepayment",
        },
      ],
    },

    {
      id: "interest-saved",

      title: "Cumulative Interest Saved",

      description:
        "Shows cumulative interest savings throughout the repayment period.",

      type: CHARTS.AREA,

      resultsKey: "interestSavedTrend",

      minWidth: 700,

      xAxis: {
        dataKey: "month",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      areas: [
        {
          dataKey: "interestSaved",
        },
      ],
    },

    {
      id: "principal-comparison",

      title: "Principal Repaid",

      description:
        "Compare cumulative principal repayment under both repayment strategies.",

      type: CHARTS.LINE,

      resultsKey: "principalComparison",

      minWidth: 700,

      xAxis: {
        dataKey: "month",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      lines: [
        {
          dataKey: "originalPrincipal",
          name: "Original",
        },
        {
          dataKey: "revisedPrincipal",
          name: "With Prepayment",
        },
      ],
    },
  ],

  tables: [
    {
      title: "Original Monthly Amortization Schedule",

      component: DataTable,

      dataKey: "originalSchedule",

      columns: [
        {
          key: "month",
          label: "Month",
          type: "number",
        },
        {
          key: "emi",
          label: "EMI",
          type: "currency",
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
        },
        {
          key: "balance",
          label: "Balance",
          type: "currency",
        },
      ],
    },

    {
      title: "Revised Monthly Amortization Schedule",

      component: DataTable,

      dataKey: "revisedSchedule",

      columns: [
        {
          key: "month",
          label: "Month",
          type: "number",
        },
        {
          key: "emi",
          label: "EMI",
          type: "currency",
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
        },
        {
          key: "prepayment",
          label: "Prepayment",
          type: "currency",
        },
        {
          key: "balance",
          label: "Balance",
          type: "currency",
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const summary = calculateLoanPrepayment(values);

    return {
      summary,

      charts: {
        interestComparison: getInterestComparisonData(summary),

        outstandingBalanceComparison:
          getOutstandingBalanceComparisonData(summary),

        interestSavedTrend: getInterestSavedTrend(summary),

        principalComparison: getPrincipalComparisonData(summary),
      },

      tables: {
        originalSchedule: summary.originalSchedule,

        revisedSchedule: summary.revisedSchedule,
      },

      metadata: {
        generatedAt: new Date().toISOString(),
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Original Schedule": results.tables.originalSchedule ?? [],

    "Revised Schedule": results.tables.revisedSchedule ?? [],
  }),

  renderInsights: ({ results }) => {
    const { summary } = results;

    const {
      interestSaved,
      interestSavingPercentage,
      monthsSaved,
      timeSavedText,
      savingsEfficiency,
      totalPrepayment,
      originalInterest,
      revisedInterest,
      revisedEmi,
      originalEmi,
      keepEmiConstant,
      prepaymentType,
      prepaymentAmount,
      startMonth,
    } = summary;

    return (
      <>
        <InsightCard type="success" title="Interest Savings">
          <p>
            By making additional prepayments, you save{" "}
            <strong>{formatCurrency(interestSaved)}</strong> in interest,
            reducing your total interest cost from{" "}
            <strong>{formatCurrency(originalInterest)}</strong> to{" "}
            <strong>{formatCurrency(revisedInterest)}</strong>.
          </p>

          <p className="mt-2">
            That's a reduction of <strong>{interestSavingPercentage}%</strong>{" "}
            in total interest.
          </p>
        </InsightCard>

        <InsightCard type="success" title="Loan Tenure Reduction">
          {monthsSaved > 0 ? (
            <>
              <p>
                Your loan closes <strong>{timeSavedText}</strong> earlier than
                the original repayment schedule.
              </p>

              <p className="mt-2">
                Keeping the loan shorter significantly reduces the amount of
                interest paid over time.
              </p>
            </>
          ) : (
            <p>
              This prepayment plan does not reduce the loan tenure
              significantly.
            </p>
          )}
        </InsightCard>

        <InsightCard type="info" title="Prepayment Summary">
          <p>
            Total planned prepayment is{" "}
            <strong>{formatCurrency(totalPrepayment)}</strong>.
          </p>

          <p className="mt-2">
            Strategy:
            <strong className="ml-1">
              {prepaymentType === "oneTime" && "One-Time"}
              {prepaymentType === "monthly" && "Monthly"}
              {prepaymentType === "quarterly" && "Quarterly"}
              {prepaymentType === "yearly" && "Yearly"}
            </strong>
          </p>

          <p className="mt-2">
            Prepayments begin from month <strong>{startMonth}</strong> with each
            payment of <strong>{formatCurrency(prepaymentAmount)}</strong>.
          </p>
        </InsightCard>

        <InsightCard
          type="info"
          title={keepEmiConstant ? "Keeping EMI Constant" : "Reducing EMI"}
        >
          {keepEmiConstant ? (
            <>
              <p>
                Your EMI remains at{" "}
                <strong>{formatCurrency(originalEmi)}</strong>.
              </p>

              <p className="mt-2">
                Every prepayment directly reduces your remaining loan tenure,
                maximizing total interest savings.
              </p>
            </>
          ) : (
            <>
              <p>
                Your EMI reduces to approximately{" "}
                <strong>{formatCurrency(revisedEmi)}</strong> after prepayments.
              </p>

              <p className="mt-2">
                This improves monthly cash flow while continuing to reduce the
                overall interest paid.
              </p>
            </>
          )}
        </InsightCard>

        <InsightCard type="warning" title="Savings Efficiency">
          <p>
            Your planned prepayments of{" "}
            <strong>{formatCurrency(totalPrepayment)}</strong> generate an
            estimated interest saving of{" "}
            <strong>{formatCurrency(interestSaved)}</strong>.
          </p>

          <p className="mt-2">
            For every ₹100 prepaid towards your loan principal, you save
            approximately <strong>{savingsEfficiency}%</strong> in future
            interest.
          </p>

          <p className="mt-2">
            Compare this with the expected long-term returns from alternative
            investments before deciding to prepay aggressively.
          </p>
        </InsightCard>
      </>
    );
  },
};

export default prepaymentCalculatorConfig;
